import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Search, Download, Upload, Loader2, Megaphone, EyeOff, IndianRupee, FileText,
  Image as ImageIcon, FileArchive,
} from 'lucide-react';
import ProductImageManagerDialog from '@/components/ProductImageManagerDialog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const PER_PAGE = 50;

const PUBLISHED_OPTIONS = [
  { value: 'all', label: 'All visibility' },
  { value: 'true', label: 'Published only' },
  { value: 'false', label: 'Hidden only' },
];
const STOCK_OPTIONS = [
  { value: 'all', label: 'Any stock' },
  { value: 'in', label: 'In stock' },
  { value: 'out', label: 'Out of stock' },
];
const STATUS_OPTIONS = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function fmtINR(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return '—'; }
}

export default function OGProductPublishing() {
  // ── Filters ───────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [parentBrand, setParentBrand] = useState('');
  const [subBrand, setSubBrand] = useState('');
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [published, setPublished] = useState('all');
  const [stock, setStock] = useState('all');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  // ── Data ──────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingPriceValue, setEditingPriceValue] = useState('');

  // Bulk price modal
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Upload state
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  // Image manager (per-row)
  const [imageManagerRow, setImageManagerRow] = useState(null);

  // Bulk zip upload state
  const zipInputRef = useRef(null);
  const [zipUploading, setZipUploading] = useState(false);
  const [zipReport, setZipReport] = useState(null);

  // Distinct dropdown sources — derived from current data + computed lazily.
  const [brandOptions, setBrandOptions] = useState([]);
  const [subBrandOptions, setSubBrandOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter change.
  useEffect(() => { setPage(1); }, [debouncedSearch, parentBrand, subBrand, category, supplier, published, stock, status]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        search: debouncedSearch || undefined,
        parent_brand: parentBrand || undefined,
        sub_brand: subBrand || undefined,
        category: category || undefined,
        supplier: supplier || undefined,
        published, stock, status,
        page, per_page: PER_PAGE,
      };
      const { data } = await axios.get(`${API}/admin/og-publishing`, {
        params, headers: authHeaders(),
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      // Refresh dropdown options from this page's rows (additive).
      setBrandOptions((prev) => Array.from(new Set([...prev, ...data.rows.map((r) => r.parent_brand).filter(Boolean)])));
      setSubBrandOptions((prev) => Array.from(new Set([...prev, ...data.rows.map((r) => r.sub_brand).filter(Boolean)])));
      setCategoryOptions((prev) => Array.from(new Set([...prev, ...data.rows.map((r) => r.category).filter(Boolean)])));
      setSupplierOptions((prev) => Array.from(new Set([...prev, ...data.rows.map((r) => r.supplier).filter(Boolean)])));
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Failed to load product publishing list');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, parentBrand, subBrand, category, supplier, published, stock, status, page]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      rows.forEach((r) => next.delete(r.id));
      setSelected(next);
    } else {
      const next = new Set(selected);
      rows.forEach((r) => next.add(r.id));
      setSelected(next);
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  // ── Inline patch (toggle / price edit) ──────────────────────────────
  const patchOne = async (id, patch, optimisticRow) => {
    if (optimisticRow) {
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...optimisticRow } : r)));
    }
    try {
      const { data } = await axios.patch(`${API}/admin/og-publishing/${id}`, patch, {
        headers: authHeaders(),
      });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...data } : r)));
      return true;
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Update failed');
      fetchRows(); // rollback
      return false;
    }
  };

  const onToggleVisibility = (row, value) => {
    patchOne(row.id, { show_on_og_app: value },
      { show_on_og_app: value });
  };

  const beginEditPrice = (row) => {
    setEditingPriceId(row.id);
    setEditingPriceValue(row.og_app_selling_price ?? '');
  };

  const commitEditPrice = async (row) => {
    const raw = (editingPriceValue ?? '').toString().trim();
    if (raw === '') {
      // Clear to null → fall back to outward price.
      const ok = await patchOne(row.id, { og_app_selling_price: null });
      if (ok) toast.success(`Cleared OG price for ${row.name}`);
    } else {
      const val = Number(raw);
      if (Number.isNaN(val) || val < 0) {
        toast.error('Enter a valid non-negative price');
        return;
      }
      const ok = await patchOne(row.id, { og_app_selling_price: val });
      if (ok) toast.success(`Updated OG price → ${fmtINR(val)} for ${row.name}`);
    }
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  // ── Bulk actions ────────────────────────────────────────────────────
  const runBulkAction = async (action, extra = {}) => {
    if (!selected.size) {
      toast.error('Select at least one product.');
      return;
    }
    setBulkSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/admin/og-publishing/bulk`, {
        product_ids: Array.from(selected), action, ...extra,
      }, { headers: authHeaders() });
      toast.success(`${action}: ${data.success_count} succeeded, ${data.failed_count} failed`);
      if (data.failed_count > 0) {
        console.warn('Bulk failures:', data.failed);
      }
      setSelected(new Set());
      setBulkPriceOpen(false);
      setBulkPriceValue('');
      fetchRows();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Bulk action failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const onBulkPriceConfirm = () => {
    const v = Number((bulkPriceValue || '').toString().trim());
    if (Number.isNaN(v) || v < 0) {
      toast.error('Enter a valid non-negative price');
      return;
    }
    runBulkAction('set_price', { og_app_selling_price: v });
  };

  // ── Upload / Template ───────────────────────────────────────────────
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/og-publishing/template`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'og-publishing-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Template download failed');
    }
  };

  const onPickUpload = () => fileInputRef.current?.click();

  const onUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/og-publishing/upload`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setUploadReport(data);
      toast.success(`Upload: ${data.success_count} applied, ${data.failed_count} failed`);
      fetchRows();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPickZipUpload = () => zipInputRef.current?.click();

  const onZipUploadChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setZipUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/og-publishing/bulk-images`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setZipReport(data);
      toast.success(`Zip upload: ${data.success_count} applied, ${data.failed_count} failed`);
      fetchRows();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Zip upload failed');
    } finally {
      setZipUploading(false);
    }
  };

  // Called by ProductImageManagerDialog when an image changes — patch row in place.
  const handleImageUpdated = (row, updated) => {
    setRows((rs) => rs.map((r) => (r.id === row.id ? {
      ...r,
      product_image: updated.product_image,
      product_images: updated.product_images,
      image_uploaded_at: updated.image_uploaded_at,
    } : r)));
  };

  const selectedCount = selected.size;
  const sortedBrand = useMemo(() => [...brandOptions].sort(), [brandOptions]);
  const sortedSubBrand = useMemo(() => [...subBrandOptions].sort(), [subBrandOptions]);
  const sortedCategory = useMemo(() => [...categoryOptions].sort(), [categoryOptions]);
  const sortedSupplier = useMemo(() => [...supplierOptions].sort(), [supplierOptions]);

  return (
    <div className="space-y-4 p-4" data-testid="og-publishing-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Megaphone className="text-orange-600" />
            OG App — Product Publishing
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Single control center for which SKUs appear in the OG Retailer App + their consumer-facing price.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="og-publishing-template-btn">
            <Download className="w-4 h-4 mr-1" /> Download Template
          </Button>
          <CsvDownloadButton onDownload={downloadTemplate} testId="og-publishing-template-csv" size="default" label="Template CSV" />
          <Button onClick={onPickUpload} disabled={uploading} data-testid="og-publishing-upload-btn"
            className="bg-orange-600 hover:bg-orange-700 text-white">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Upload Excel
          </Button>
          <Button onClick={onPickZipUpload} disabled={zipUploading} variant="outline"
            data-testid="og-publishing-zip-btn">
            {zipUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileArchive className="w-4 h-4 mr-1" />}
            Bulk Image Zip
          </Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.csv"
            onChange={onUploadChange} className="hidden"
            data-testid="og-publishing-upload-input" />
          <input ref={zipInputRef} type="file" accept=".zip"
            onChange={onZipUploadChange} className="hidden"
            data-testid="og-publishing-zip-input" />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search Product ID / Barcode / Name"
                className="pl-9" data-testid="og-publishing-search" />
            </div>
            <FilterDropdown value={parentBrand} onValueChange={setParentBrand}
              placeholder="Parent Brand" options={sortedBrand} testid="og-filter-parent-brand" />
            <FilterDropdown value={subBrand} onValueChange={setSubBrand}
              placeholder="Sub Brand" options={sortedSubBrand} testid="og-filter-sub-brand" />
            <FilterDropdown value={category} onValueChange={setCategory}
              placeholder="Category" options={sortedCategory} testid="og-filter-category" />
            <FilterDropdown value={supplier} onValueChange={setSupplier}
              placeholder="Supplier" options={sortedSupplier} testid="og-filter-supplier" />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <ChipGroup value={published} onChange={setPublished}
              options={PUBLISHED_OPTIONS} testidPrefix="og-chip-published" />
            <ChipGroup value={stock} onChange={setStock}
              options={STOCK_OPTIONS} testidPrefix="og-chip-stock" />
            <ChipGroup value={status} onChange={setStatus}
              options={STATUS_OPTIONS} testidPrefix="og-chip-status" />
            <div className="ml-auto text-xs text-slate-500">
              {loading ? 'Loading…' : `Showing ${rows.length} of ${total} products`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <Badge className="bg-orange-600 text-white" data-testid="og-bulk-selected-count">
              {selectedCount} selected
            </Badge>
            <Button size="sm" disabled={bulkSubmitting} onClick={() => runBulkAction('publish')}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="og-bulk-publish-btn">
              <Megaphone className="w-4 h-4 mr-1" /> Publish to OG App
            </Button>
            <Button size="sm" disabled={bulkSubmitting} variant="outline"
              onClick={() => runBulkAction('unpublish')}
              data-testid="og-bulk-unpublish-btn">
              <EyeOff className="w-4 h-4 mr-1" /> Remove from OG App
            </Button>
            <Button size="sm" disabled={bulkSubmitting} variant="outline"
              onClick={() => setBulkPriceOpen(true)}
              data-testid="og-bulk-price-btn">
              <IndianRupee className="w-4 h-4 mr-1" /> Bulk Price Update
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}
              data-testid="og-bulk-clear-btn">
              Clear selection
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                    data-testid="og-select-all" />
                </th>
                <th className="px-3 py-3 w-12 text-center">Image</th>
                <th className="px-3 py-3 text-left">Product ID</th>
                <th className="px-3 py-3 text-left">Barcode</th>
                <th className="px-3 py-3 text-left">Product</th>
                <th className="px-3 py-3 text-left">Parent / Sub Brand</th>
                <th className="px-3 py-3 text-left">Category</th>
                <th className="px-3 py-3 text-right">Stock</th>
                <th className="px-3 py-3 text-right">ERP Selling Price</th>
                <th className="px-3 py-3 text-right">OG App Selling Price</th>
                <th className="px-3 py-3 text-center">Show on OG App</th>
                <th className="px-3 py-3 text-left">Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={12} className="text-center py-12 text-slate-500">
                  <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />Loading…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={12} className="text-center py-12 text-slate-500">
                  No products match the current filters.
                </td></tr>
              ) : rows.map((r) => {
                const inStock = (Number(r.current_stock) || 0) > 0;
                const isEditing = editingPriceId === r.id;
                return (
                  <tr key={r.id} className="border-t hover:bg-slate-50"
                      data-testid={`og-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleOne(r.id)}
                        data-testid={`og-row-checkbox-${r.id}`} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => setImageManagerRow(r)}
                        className="relative inline-block w-10 h-10 rounded border bg-slate-50 overflow-hidden hover:ring-2 hover:ring-orange-400 transition"
                        title="Click to edit image"
                        data-testid={`og-row-thumb-${r.id}`}>
                        {r.product_image ? (
                          <img src={r.product_image} alt=""
                            className="w-full h-full object-cover"
                            data-testid={`og-row-thumb-img-${r.id}`} />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-slate-300 m-auto mt-2.5" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-blue-700">{r.product_id || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.barcode || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.measurement_unit}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{r.parent_brand || '—'}</div>
                      <div className="text-slate-400">{r.sub_brand || '—'}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.category || '—'}</td>
                    <td className="px-3 py-2 text-right">
                      {inStock ? (
                        <span className="text-emerald-700 font-medium">{r.current_stock}</span>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 border border-orange-300"
                               data-testid={`og-row-oos-${r.id}`}>Out of Stock</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">
                      {fmtINR(r.outward_selling_price)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isEditing ? (
                        <div className="flex items-center justify-end gap-1">
                          <Input
                            type="number" step="0.01" min="0"
                            value={editingPriceValue}
                            onChange={(e) => setEditingPriceValue(e.target.value)}
                            onBlur={() => commitEditPrice(r)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEditPrice(r);
                              if (e.key === 'Escape') { setEditingPriceId(null); setEditingPriceValue(''); }
                            }}
                            autoFocus
                            className="w-24 h-8 text-right"
                            data-testid={`og-row-price-input-${r.id}`}
                          />
                        </div>
                      ) : (
                        <button
                          className="font-semibold text-orange-700 hover:underline"
                          onClick={() => beginEditPrice(r)}
                          data-testid={`og-row-price-${r.id}`}>
                          {r.og_app_selling_price === null || r.og_app_selling_price === undefined
                            ? <span className="text-slate-400 font-normal">— (uses ERP)</span>
                            : fmtINR(r.og_app_selling_price)}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Switch
                        checked={!!r.show_on_og_app}
                        onCheckedChange={(v) => onToggleVisibility(r, v)}
                        data-testid={`og-row-toggle-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      <div>{fmtDateTime(r.og_last_updated_at)}</div>
                      <div className="text-slate-400">{r.og_last_updated_by_name || ''}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <div className="text-slate-500">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="og-page-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              data-testid="og-page-next">Next</Button>
          </div>
        </div>
      )}

      {/* Bulk Price Modal */}
      <Dialog open={bulkPriceOpen} onOpenChange={setBulkPriceOpen}>
        <DialogContent data-testid="og-bulk-price-modal">
          <DialogHeader>
            <DialogTitle>Bulk OG App Price Update</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            Set OG App Selling Price (GST-inclusive) for the <b>{selectedCount}</b> selected product
            {selectedCount === 1 ? '' : 's'}. ERP pricing is not affected.
          </p>
          <Input type="number" step="0.01" min="0" placeholder="e.g. 95.00"
            value={bulkPriceValue} onChange={(e) => setBulkPriceValue(e.target.value)}
            data-testid="og-bulk-price-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPriceOpen(false)}>Cancel</Button>
            <Button onClick={onBulkPriceConfirm} disabled={bulkSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="og-bulk-price-confirm">
              {bulkSubmitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Apply to {selectedCount} product{selectedCount === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload report */}
      <Dialog open={!!uploadReport} onOpenChange={(o) => !o && setUploadReport(null)}>
        <DialogContent className="max-w-2xl" data-testid="og-upload-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4" /> Upload Report
            </DialogTitle>
          </DialogHeader>
          {uploadReport && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total rows" value={uploadReport.total_rows} />
                <Stat label="Applied" value={uploadReport.success_count} accent="text-emerald-700" />
                <Stat label="Failed" value={uploadReport.failed_count} accent="text-rose-700" />
              </div>
              {uploadReport.failed?.length > 0 && (
                <div className="border border-rose-200 rounded p-2 bg-rose-50 max-h-72 overflow-y-auto">
                  <p className="font-semibold text-rose-800 mb-2">Failed rows ({uploadReport.failed.length})</p>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left">
                      <th className="pr-2">Row</th><th className="pr-2">Product ID</th>
                      <th className="pr-2">Barcode</th><th>Error</th>
                    </tr></thead>
                    <tbody>
                      {uploadReport.failed.map((f, i) => (
                        <tr key={i} className="border-t border-rose-200">
                          <td className="pr-2 py-1">{f.row_number}</td>
                          <td className="pr-2 py-1 font-mono">{f.product_id || '—'}</td>
                          <td className="pr-2 py-1 font-mono">{f.barcode || '—'}</td>
                          <td className="py-1">{f.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setUploadReport(null)} data-testid="og-upload-report-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Image manager (per-row) */}
      <ProductImageManagerDialog
        open={!!imageManagerRow}
        onOpenChange={(o) => !o && setImageManagerRow(null)}
        productId={imageManagerRow?.id}
        productName={imageManagerRow?.name}
        initialImage={imageManagerRow?.product_image}
        initialGallery={imageManagerRow?.product_images}
        onUpdated={(updated) => imageManagerRow && handleImageUpdated(imageManagerRow, updated)}
      />

      {/* Zip upload report */}
      <Dialog open={!!zipReport} onOpenChange={(o) => !o && setZipReport(null)}>
        <DialogContent className="max-w-2xl" data-testid="og-zip-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileArchive className="w-4 h-4" /> Bulk Image Zip Report
            </DialogTitle>
          </DialogHeader>
          {zipReport && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Total entries" value={zipReport.total_entries} />
                <Stat label="Uploaded" value={zipReport.success_count} accent="text-emerald-700" />
                <Stat label="Skipped" value={zipReport.failed_count} accent="text-rose-700" />
              </div>
              {zipReport.success?.length > 0 && (
                <div className="border border-emerald-200 rounded p-2 bg-emerald-50 max-h-40 overflow-y-auto">
                  <p className="font-semibold text-emerald-800 mb-1">Uploaded</p>
                  <ul className="text-xs space-y-0.5">
                    {zipReport.success.map((s, i) => (
                      <li key={i}>
                        <span className="font-mono">{s.filename}</span> → {s.resolved_product_name || s.product_code}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {zipReport.failed?.length > 0 && (
                <div className="border border-rose-200 rounded p-2 bg-rose-50 max-h-60 overflow-y-auto">
                  <p className="font-semibold text-rose-800 mb-1">Skipped</p>
                  <ul className="text-xs space-y-0.5">
                    {zipReport.failed.map((f, i) => (
                      <li key={i}>
                        <span className="font-mono">{f.filename}</span> — {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setZipReport(null)} data-testid="og-zip-report-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterDropdown({ value, onValueChange, placeholder, options, testid }) {
  return (
    <Select value={value || '__all__'}
      onValueChange={(v) => onValueChange(v === '__all__' ? '' : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{placeholder} — All</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ChipGroup({ value, onChange, options, testidPrefix }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          data-testid={`${testidPrefix}-${o.value}`}
          className={`px-3 py-1 rounded-full text-xs border transition ${
            value === o.value
              ? 'bg-slate-900 text-white border-slate-900'
              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border rounded p-2 text-center">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${accent || 'text-slate-800'}`}>{value}</div>
    </div>
  );
}
